// Response formatter service for converting backend data to business intelligence

// Business Intelligence Response Types
export interface BusinessInsight {
  type: 'summary' | 'trend' | 'alert' | 'recommendation';
  title: string;
  description: string;
  severity?: 'low' | 'medium' | 'high' | 'critical';
  actionable?: boolean;
  actionText?: string;
}

export interface DataVisualization {
  type: 'chart' | 'table' | 'metric' | 'progress';
  title: string;
  data: any;
  config?: any;
}

export interface FormattedResponse {
  summary: string;
  insights: BusinessInsight[];
  visualizations: DataVisualization[];
  followUpQuestions: string[];
  businessContext: string;
  dataTable?: any[];
  exportData?: any[];
}

// Marketplace Configuration
const MARKETPLACE_CONFIG = {
  24: { name: 'Target', color: '#FF6B35', priority: 'high' },
  2: { name: 'Amazon USA', color: '#FF9900', priority: 'high' },
  11: { name: 'Direct', color: '#1976D2', priority: 'medium' },
  10: { name: 'Walmart', color: '#0071CE', priority: 'medium' }
};

// Business Rules and Context
const BUSINESS_CONTEXT = {
  warehouseHours: '6:00 AM - 2:30 PM PST',
  shippingCutoff: '1:00 PM PST for same-day shipping',
  peakDays: ['Monday', 'Tuesday'],
  averageOrderValue: 45.67,
  typicalLeadTime: '2-3 business days'
};

export class ResponseFormatter {
  
  /**
   * Main method to format backend responses into business intelligence
   */
  static formatResponse(
    userQuery: string, 
    backendResponse: any, 
    queryType?: string
  ): FormattedResponse {
    
    // Handle different response types
    if (backendResponse.error) {
      return this.formatErrorResponse(userQuery, backendResponse);
    }
    
    if (backendResponse.rowsReturned === 0) {
      return this.formatNoDataResponse(userQuery, backendResponse);
    }
    
    // Route to specific formatters based on query type
    const queryLower = userQuery.toLowerCase();
    
    if (queryLower.includes('revenue') || queryLower.includes('sales')) {
      return this.formatRevenueResponse(userQuery, backendResponse);
    }
    
    if (queryLower.includes('order') || queryLower.includes('ship')) {
      return this.formatOrderResponse(userQuery, backendResponse);
    }
    
    if (queryLower.includes('inventory') || queryLower.includes('stock')) {
      return this.formatInventoryResponse(userQuery, backendResponse);
    }
    
    if (queryLower.includes('channel') || queryLower.includes('marketplace')) {
      return this.formatChannelResponse(userQuery, backendResponse);
    }
    
    // Default formatter for other queries
    return this.formatGenericResponse(userQuery, backendResponse);
  }
  
  /**
   * Format revenue and sales related queries
   */
  private static formatRevenueResponse(userQuery: string, backendResponse: any): FormattedResponse {
    const data = backendResponse.data || [];
    const totalRevenue = data.reduce((sum: number, item: any) => 
      sum + (item.FinalGrandTotal || item.Revenue || item.Amount || 0), 0);
    
    const orderCount = data.length;
    const averageOrderValue = orderCount > 0 ? totalRevenue / orderCount : 0;
    
    // Group by date if available
    const revenueByDate = this.groupByDate(data, 'OrderDate', 'FinalGrandTotal');
    
    const insights: BusinessInsight[] = [
      {
        type: 'summary',
        title: 'Revenue Summary',
        description: `Total revenue: $${totalRevenue.toLocaleString()} from ${orderCount} orders`,
        actionable: false
      }
    ];
    
    if (averageOrderValue > 0) {
      insights.push({
        type: 'trend',
        title: 'Average Order Value',
        description: `AOV: $${averageOrderValue.toFixed(2)}`,
        actionable: false
      });
    }
    
    // Add trend insights
    if (revenueByDate.length > 1) {
      const trend = this.calculateTrend(revenueByDate.map(d => d.value));
      if (trend > 0.1) {
        insights.push({
          type: 'trend',
          title: 'Positive Revenue Trend',
          description: `Revenue is trending upward by ${(trend * 100).toFixed(1)}%`,
          severity: 'low',
          actionable: true,
          actionText: 'Consider increasing inventory for high-performing products'
        });
      }
    }
    
    const visualizations: DataVisualization[] = [
      {
        type: 'metric',
        title: 'Total Revenue',
        data: { value: totalRevenue, format: 'currency' }
      },
      {
        type: 'chart',
        title: 'Revenue Trend',
        data: revenueByDate,
        config: { type: 'line', xAxis: 'date', yAxis: 'revenue' }
      }
    ];
    
    return {
      summary: `Your revenue analysis shows $${totalRevenue.toLocaleString()} in total sales from ${orderCount} orders.`,
      insights,
      visualizations,
      followUpQuestions: [
        'How does this compare to last week/month?',
        'Which sales channels are performing best?',
        'What are the top-selling products by revenue?',
        'Are there any seasonal trends I should know about?'
      ],
      businessContext: `Based on your ${BUSINESS_CONTEXT.warehouseHours} operations and ${BUSINESS_CONTEXT.shippingCutoff} shipping cutoff.`,
      dataTable: data,
      exportData: data
    };
  }
  
  /**
   * Format order and shipping related queries
   */
  private static formatOrderResponse(userQuery: string, backendResponse: any): FormattedResponse {
    const data = backendResponse.data || [];
    
    // Analyze order statuses
    const statusCounts = this.countByField(data, 'ShipmentStatus') || 
                        this.countByField(data, 'Status') ||
                        this.countByField(data, 'Issue');
    
    const totalOrders = data.length;
    const problemOrders = data.filter((item: any) => 
      item.ShipmentStatus === 'Not Shipped' || 
      item.Status === 'Problem' ||
      item.Issue === 'Stuck in Packing'
    ).length;
    
    const insights: BusinessInsight[] = [
      {
        type: 'summary',
        title: 'Order Status Overview',
        description: `${totalOrders} total orders with ${problemOrders} requiring attention`,
        actionable: false
      }
    ];
    
    if (problemOrders > 0) {
      insights.push({
        type: 'alert',
        title: 'Orders Requiring Attention',
        description: `${problemOrders} orders have shipping or processing issues`,
        severity: problemOrders > 5 ? 'high' : 'medium',
        actionable: true,
        actionText: 'Review warehouse processing queue'
      });
    }
    
    // Check for old orders
    const oldOrders = data.filter((item: any) => {
      const orderDate = new Date(item.OrderDate);
      const daysOld = (Date.now() - orderDate.getTime()) / (1000 * 60 * 60 * 24);
      return daysOld > 3;
    });
    
    if (oldOrders.length > 0) {
      insights.push({
        type: 'alert',
        title: 'Aging Orders',
        description: `${oldOrders.length} orders are older than 3 days`,
        severity: 'high',
        actionable: true,
        actionText: 'Prioritize these orders for immediate processing'
      });
    }
    
    const visualizations: DataVisualization[] = [
      {
        type: 'chart',
        title: 'Order Status Breakdown',
        data: statusCounts,
        config: { type: 'pie', showPercentages: true }
      },
      {
        type: 'metric',
        title: 'Problem Orders',
        data: { value: problemOrders, total: totalOrders, format: 'percentage' }
      }
    ];
    
    return {
      summary: `Order analysis shows ${totalOrders} orders with ${problemOrders} requiring immediate attention.`,
      insights,
      visualizations,
      followUpQuestions: [
        'What\'s causing the shipping delays?',
        'Which products are most commonly backordered?',
        'How does this compare to our typical processing times?',
        'What\'s the warehouse capacity for today?'
      ],
      businessContext: `Warehouse operates ${BUSINESS_CONTEXT.warehouseHours} with ${BUSINESS_CONTEXT.shippingCutoff} cutoff.`,
      dataTable: data,
      exportData: data
    };
  }
  
  /**
   * Format inventory and stock related queries
   */
  private static formatInventoryResponse(userQuery: string, backendResponse: any): FormattedResponse {
    const data = backendResponse.data || [];
    
    const totalProducts = data.length;
    const outOfStock = data.filter((item: any) => 
      item.QOH === 0 || item.CurrentStock === 0 || item.QuantityOnHand === 0
    ).length;
    
    const lowStock = data.filter((item: any) => {
      const stock = item.QOH || item.CurrentStock || item.QuantityOnHand || 0;
      const reorderPoint = item.ReorderPoint || item.ReorderLevel || 0;
      return stock > 0 && stock <= reorderPoint;
    }).length;
    
    const totalValue = data.reduce((sum: number, item: any) => {
      const stock = item.QOH || item.CurrentStock || item.QuantityOnHand || 0;
      const cost = item.Cost || 0;
      return sum + (stock * cost);
    }, 0);
    
    const insights: BusinessInsight[] = [
      {
        type: 'summary',
        title: 'Inventory Status',
        description: `${totalProducts} products tracked with $${totalValue.toLocaleString()} in inventory value`,
        actionable: false
      }
    ];
    
    if (outOfStock > 0) {
      insights.push({
        type: 'alert',
        title: 'Out of Stock Items',
        description: `${outOfStock} products are completely out of stock`,
        severity: 'critical',
        actionable: true,
        actionText: 'Generate purchase orders for out-of-stock items'
      });
    }
    
    if (lowStock > 0) {
      insights.push({
        type: 'alert',
        title: 'Low Stock Alerts',
        description: `${lowStock} products are below reorder points`,
        severity: 'high',
        actionable: true,
        actionText: 'Review reorder quantities and lead times'
      });
    }
    
    // Check for slow-moving inventory
    const slowMoving = data.filter((item: any) => {
      const lastSold = item.LastSoldDate;
      if (!lastSold) return false;
      const daysSinceSold = (Date.now() - new Date(lastSold).getTime()) / (1000 * 60 * 60 * 24);
      return daysSinceSold > 90;
    }).length;
    
    if (slowMoving > 0) {
      insights.push({
        type: 'recommendation',
        title: 'Slow-Moving Inventory',
        description: `${slowMoving} products haven't sold in 90+ days`,
        severity: 'medium',
        actionable: true,
        actionText: 'Consider promotions or liquidation strategies'
      });
    }
    
    const visualizations: DataVisualization[] = [
      {
        type: 'chart',
        title: 'Stock Status Distribution',
        data: [
          { name: 'In Stock', value: totalProducts - outOfStock - lowStock },
          { name: 'Low Stock', value: lowStock },
          { name: 'Out of Stock', value: outOfStock }
        ],
        config: { type: 'pie', colors: ['#4CAF50', '#FF9800', '#F44336'] }
      },
      {
        type: 'metric',
        title: 'Inventory Value',
        data: { value: totalValue, format: 'currency' }
      }
    ];
    
    return {
      summary: `Inventory analysis shows ${totalProducts} products with ${outOfStock} out of stock and ${lowStock} below reorder points.`,
      insights,
      visualizations,
      followUpQuestions: [
        "What's the lead time for restocking these items?",
        'Which suppliers should I contact first?',
        'Are there any seasonal inventory patterns?',
        "What is the cost impact of these stockouts?"
      ],
      businessContext: `Based on your typical ${BUSINESS_CONTEXT.typicalLeadTime} lead time for restocking.`,
      dataTable: data,
      exportData: data
    };
  }
  
  /**
   * Format channel and marketplace related queries
   */
  private static formatChannelResponse(userQuery: string, backendResponse: any): FormattedResponse {
    const data = backendResponse.data || [];
    
    // Group by CartID (marketplace)
    const channelPerformance = this.groupByField(data, 'CartID', 'FinalGrandTotal');
    
    const insights: BusinessInsight[] = [
      {
        type: 'summary',
        title: 'Channel Performance',
        description: `Performance across ${Object.keys(channelPerformance).length} sales channels`,
        actionable: false
      }
    ];
    
    // Find top performing channel
    let topChannel = '';
    let topRevenue = 0;
    
    Object.entries(channelPerformance).forEach(([cartId, revenue]) => {
      if (revenue > topRevenue) {
        topRevenue = revenue;
        const cartIdNum = parseInt(cartId);
        topChannel = MARKETPLACE_CONFIG[cartIdNum as keyof typeof MARKETPLACE_CONFIG]?.name || `Channel ${cartId}`;
      }
    });
    
    if (topChannel) {
      insights.push({
        type: 'trend',
        title: 'Top Performing Channel',
        description: `${topChannel} is generating the highest revenue at $${topRevenue.toLocaleString()}`,
        actionable: true,
        actionText: 'Consider increasing inventory allocation for this channel'
      });
    }
    
    // Check for underperforming channels
    const avgRevenue = Object.values(channelPerformance).reduce((sum: number, rev: number) => sum + rev, 0) / Object.keys(channelPerformance).length;
    
    Object.entries(channelPerformance).forEach(([cartId, revenue]) => {
      if (revenue < avgRevenue * 0.5) {
        const cartIdNum = parseInt(cartId);
        const channelName = MARKETPLACE_CONFIG[cartIdNum as keyof typeof MARKETPLACE_CONFIG]?.name || `Channel ${cartId}`;
        insights.push({
          type: 'recommendation',
          title: 'Channel Optimization Opportunity',
          description: `${channelName} is underperforming at $${revenue.toLocaleString()}`,
          severity: 'medium',
          actionable: true,
          actionText: 'Review pricing and inventory strategy for this channel'
        });
      }
    });
    
    const visualizations: DataVisualization[] = [
      {
        type: 'chart',
        title: 'Channel Revenue Breakdown',
        data: Object.entries(channelPerformance).map(([cartId, revenue]) => {
          const cartIdNum = parseInt(cartId);
          return {
            name: MARKETPLACE_CONFIG[cartIdNum as keyof typeof MARKETPLACE_CONFIG]?.name || `Channel ${cartId}`,
            value: revenue,
            color: MARKETPLACE_CONFIG[cartIdNum as keyof typeof MARKETPLACE_CONFIG]?.color
          };
        }),
        config: { type: 'bar', xAxis: 'channel', yAxis: 'revenue' }
      }
    ];
    
    return {
      summary: `Channel analysis shows ${topChannel} as the top performer with $${topRevenue.toLocaleString()} in revenue.`,
      insights,
      visualizations,
      followUpQuestions: [
        'How do these channels compare to last month?',
        'What\'s driving the performance differences?',
        'Are there inventory allocation opportunities?',
        'Which channels have the highest profit margins?'
      ],
      businessContext: `Based on your multi-channel strategy across ${Object.keys(channelPerformance).length} marketplaces.`,
      dataTable: data,
      exportData: data
    };
  }
  
  /**
   * Format generic responses for other query types
   */
  private static formatGenericResponse(userQuery: string, backendResponse: any): FormattedResponse {
    const data = backendResponse.data || [];
    
    const insights: BusinessInsight[] = [
      {
        type: 'summary',
        title: 'Data Analysis Complete',
        description: `Found ${data.length} records matching your query`,
        actionable: false
      }
    ];
    
    // Try to identify patterns in the data
    if (data.length > 10) {
      insights.push({
        type: 'trend',
        title: 'Large Dataset',
        description: `This is a substantial dataset with ${data.length} records`,
        actionable: true,
        actionText: 'Consider filtering for more specific insights'
      });
    }
    
    const visualizations: DataVisualization[] = [
      {
        type: 'metric',
        title: 'Records Found',
        data: { value: data.length, format: 'number' }
      }
    ];
    
    return {
      summary: `Analysis complete! Found ${data.length} records for your query.`,
      insights,
      visualizations,
      followUpQuestions: [
        'Would you like to see this data in a different format?',
        'Should I analyze any specific aspects of this data?',
        'Would you like to export this data for further analysis?',
        'Are there any trends or patterns you\'d like me to identify?'
      ],
      businessContext: 'Based on your current business data and operational context.',
      dataTable: data,
      exportData: data
    };
  }
  
  /**
   * Format error responses
   */
  private static formatErrorResponse(userQuery: string, backendResponse: any): FormattedResponse {
    return {
      summary: 'I encountered an issue processing your request.',
      insights: [{
        type: 'alert',
        title: 'Processing Error',
        description: backendResponse.error || 'Unknown error occurred',
        severity: 'high',
        actionable: false
      }],
      visualizations: [],
      followUpQuestions: [
        'Could you rephrase your question?',
        'Would you like to try a different approach?',
        'Should I check the system status?'
      ],
      businessContext: 'The system encountered a technical issue while processing your request.',
      dataTable: [],
      exportData: []
    };
  }
  
  /**
   * Format no-data responses
   */
  private static formatNoDataResponse(userQuery: string, backendResponse: any): FormattedResponse {
    return {
      summary: 'No data found matching your criteria.',
      insights: [{
        type: 'summary',
        title: 'No Results',
        description: 'Your query returned no matching records',
        actionable: false
      }],
      visualizations: [],
      followUpQuestions: [
        'Would you like to broaden your search criteria?',
        'Should I check if the data exists in a different time period?',
        'Would you like to see similar data from a different perspective?'
      ],
      businessContext: 'Based on your current query parameters and available data.',
      dataTable: [],
      exportData: []
    };
  }
  
  // Helper methods
  private static groupByField(data: any[], field: string, valueField?: string) {
    const grouped: Record<string, any> = {};
    
    data.forEach(item => {
      const key = item[field] || 'Unknown';
      if (valueField) {
        grouped[key] = (grouped[key] || 0) + (item[valueField] || 0);
      } else {
        grouped[key] = (grouped[key] || 0) + 1;
      }
    });
    
    return grouped;
  }
  
  private static groupByDate(data: any[], dateField: string, valueField: string) {
    const grouped: Record<string, number> = {};
    
    data.forEach(item => {
      const date = new Date(item[dateField]);
      const dateKey = date.toLocaleDateString();
      grouped[dateKey] = (grouped[dateKey] || 0) + (item[valueField] || 0);
    });
    
    return Object.entries(grouped).map(([date, value]) => ({ date, value }));
  }
  
  private static countByField(data: any[], field: string) {
    return this.groupByField(data, field);
  }
  
  private static calculateTrend(values: number[]): number {
    if (values.length < 2) return 0;
    
    const firstHalf = values.slice(0, Math.floor(values.length / 2));
    const secondHalf = values.slice(Math.floor(values.length / 2));
    
    const firstAvg = firstHalf.reduce((sum, val) => sum + val, 0) / firstHalf.length;
    const secondAvg = secondHalf.reduce((sum, val) => sum + val, 0) / secondHalf.length;
    
    if (firstAvg === 0) return 0;
    return (secondAvg - firstAvg) / firstAvg;
  }
}
